import React, { useState } from "react";
import { Navbar2 } from "../../Components/Navbar2/Navbar2";
import Footer from "../../Components/Footer/Footer";
import useSubscription from "./useSubscription";
import SubscriptionCard from "../../Components/SubscriptionCard";
import {
  SparklesIcon,
  ShieldCheckIcon,
  BoltIcon,
  TvIcon,
} from "@heroicons/react/24/solid";
import FooterNew from "../../Components/LandingNew/FooterNew";


const comparisonFeatures = [
  {
    name: "Access to Standard Free Content",
    free: "Included (With Ads)",
    premium: "100% Ad-Free & Uninterrupted",
  },
  {
    name: "Unlimited Access to Exclusive VIP Titles",
    free: "Not Available",
    premium: "Unlimited Access",
  },
  {
    name: "Video & Audio Quality",
    free: "Standard Definition",
    premium: "Ultra HD 4K & Hi-Res Audio",
  },
  {
    name: "E-Books & Audiobooks Library Access",
    free: "Preview Only",
    premium: "Full Library Access",
  },
  {
    name: "Priority Customer Support",
    free: "Standard Support",
    premium: "24/7 VIP Priority Support",
  },
  {
    name: "Device Compatibility",
    free: "All Devices",
    premium: "All Devices + Simultaneous Streams",
  },
];

const subscriptionFaqs = [
  {
    q: "What is included with a BAB Premium Subscription?",
    a: "BAB Premium gives you unlimited, unrestricted access to all Exclusive VIP movies, audiobooks, ebooks, and music on our platform. Plus, you can stream all free titles completely ad-free with enhanced streaming quality.",
  },
  {
    q: "Can I cancel my subscription at any time?",
    a: "Yes! There are no lock-in contracts or cancellation fees. You can cancel anytime directly from your Account Profile with a single click. Your access will remain active until the end of your billing cycle.",
  },
  {
    q: "What payment methods are supported?",
    a: "We support all major credit and debit cards (Visa, MasterCard, American Express) processed securely with 256-bit encryption via Stripe.",
  },
  {
    q: "How does the Yearly plan discount work?",
    a: "The BAB Premium Yearly plan costs $50.00 for a full 360-day year (equivalent to just $4.16/month), saving you over 30% compared to paying $6.00 month-to-month ($72/year total).",
  },
];

export default function Subscription() {
  const {
    packages,
    packagesLoading,
    onSelectPlan,
    isSubscribed,
  } = useSubscription();


  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);

  // Sort packages: Yearly first
  const sortedPackages = [...(packages || [])].sort((a, b) => b.duration - a.duration);

  return (
    <div className="min-h-screen bab-new-bg text-white flex flex-col justify-between">
      <Navbar2 />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6 lg:px-14 text-center">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[35rem] h-[35rem] bg-secondary/15 rounded-full blur-[140px] pointer-events-none -z-10" />

          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary/15 border border-secondary/30 text-secondary text-xs sm:text-sm font-extrabold uppercase tracking-widest mb-6">
              <SparklesIcon className="w-4 h-4" />
              <span>Choose Your VIP Access</span>
            </div>

            <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6">
              Unlimited Exclusive Content. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-amber-400">
                100% Ad-Free Streaming.
              </span>
            </h1>

            <p className="text-base sm:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-10">
              Join BAB Premium to unlock our full catalog of subscriber-only movies,
              audiobooks, and books with zero ads and maximum quality.
            </p>

            {isSubscribed && (
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-300 font-bold text-sm mb-8 shadow-lg shadow-amber-950/30">
                <SparklesIcon className="w-5 h-5 text-amber-400" />
                <span>You are currently an active BAB Premium VIP Member!</span>
              </div>
            )}
          </div>

          {/* Pricing Cards Grid */}
          <div className="max-w-5xl mx-auto mt-4">
            {packagesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-[30rem] bg-zinc-900/40 rounded-3xl border border-zinc-850"
                  ></div>
                ))}
              </div>
            ) : sortedPackages.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch text-left">
                {sortedPackages.map((pkg, idx) => {
                  const isYearly =
                    pkg.duration >= 300 ||
                    String(pkg.title).toLowerCase().includes("year");
                  return (
                    <SubscriptionCard
                      key={pkg.id || idx}
                      pkg={pkg}
                      isPopular={isYearly}
                      onSelectPlan={onSelectPlan}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 text-zinc-400">
                Subscription packages are being updated. Please check back shortly.
              </div>
            )}
          </div>
        </section>

        {/* Feature Highlights Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-14 border-t border-zinc-800/80 bg-zinc-950/40">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-secondary text-xs uppercase font-extrabold tracking-widest block mb-2">
                Why Go Premium
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Everything You Need for Endless Entertainment
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 rounded-2xl bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700 transition-all">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary mb-4">
                  <SparklesIcon className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-lg text-white mb-2">
                  VIP Exclusive Content
                </h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Instant access to our premium vault of movies, full-length audio tracks, and steel book releases.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700 transition-all">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary mb-4">
                  <TvIcon className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-lg text-white mb-2">
                  100% Ad-Free Experience
                </h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Say goodbye to video and banner ads. Enjoy continuous, uninterrupted watching and reading.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700 transition-all">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary mb-4">
                  <BoltIcon className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-lg text-white mb-2">
                  Maximum Audio & HD Quality
                </h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Stream in pristine high-definition and lossless studio master audio quality across all devices.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700 transition-all">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary mb-4">
                  <ShieldCheckIcon className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-lg text-white mb-2">
                  No Commitment Required
                </h4>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Flexibility first. You can easily switch plans or cancel online with zero hassle at any time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Plan Comparison Table */}
        <section className="py-16 px-4 sm:px-6 lg:px-14 border-t border-zinc-800/80">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-secondary text-xs uppercase font-extrabold tracking-widest block mb-2">
                Side-by-Side Comparison
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Compare Free vs. BAB Premium
              </h2>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-zinc-800 bg-zinc-900/30">
              <table className="w-full text-left text-sm text-zinc-300">
                <thead className="bg-zinc-900/80 text-xs uppercase tracking-wider text-zinc-400 border-b border-zinc-800">
                  <tr>
                    <th scope="col" className="px-6 py-4 font-bold">
                      Feature
                    </th>
                    <th scope="col" className="px-6 py-4 font-bold text-center">
                      Free User
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 font-bold text-center text-secondary bg-secondary/10"
                    >
                      BAB Premium VIP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {comparisonFeatures.map((row, idx) => (
                    <tr key={idx} className="hover:bg-zinc-900/50 transition-colors">
                      <td className="px-6 py-4 font-medium text-white">
                        {row.name}
                      </td>
                      <td className="px-6 py-4 text-center text-zinc-400">
                        {row.free}
                      </td>
                      <td className="px-6 py-4 text-center font-bold text-secondary bg-secondary/5">
                        {row.premium}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ Accordion Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-14 border-t border-zinc-800/80 bg-zinc-950/40">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-secondary text-xs uppercase font-extrabold tracking-widest block mb-2">
                Got Questions?
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-3">
              {subscriptionFaqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full p-5 text-left font-bold text-sm sm:text-base flex items-center justify-between text-white hover:text-secondary transition-colors"
                  >
                    <span>{faq.q}</span>
                    <span
                      className={`text-zinc-400 text-lg transition-transform ${openFaq === idx ? "rotate-45" : ""
                        }`}
                    >
                      +
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5 text-xs sm:text-sm text-zinc-400 leading-relaxed border-t border-zinc-800/60 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
}
